<template>
  <Component :is="viewLayout">
    <template #page-heading>
      <h1 class="heading heading--large">
        Insight dos leads
      </h1>
    </template>

    <template #page-filter>
      <CommonPageFilter
        :filters="[
          'dateRange',
          'action'
        ]"
      />
    </template>

    <template #main>
      <div class="leads-insight">
        <BaseGrid columns="4">
          <BasePaneCard
            heading="Leads importados"
            :isLoading="isLoadingImported"
            class="grid-span-1"
          >
            <span class="label label--large">
              {{ importedLeads | number }}
            </span>
          </BasePaneCard>

          <BasePaneCard
            heading="Leads qualificados"
            :isLoading="isLoadingQualified"
            class="grid-span-1"
          >
            <span class="label label--large">
              {{ qualifiedLeads | number }}
            </span>
          </BasePaneCard>

          <BasePaneCard
            highlighted
            class="grid-span-2"
          >
            <BaseGrid columns="2">
              <BasePaneCard
                :isLoading="isLoadingInsights"
                heading="Previsão de receita"
                unboxed
                class="leads-insight__divider"
              >
                <span class="label label--large">
                  {{ predictedIncome | currency('integer') }}
                </span>
              </BasePaneCard>

              <BasePaneCard
                :isLoading="isLoadingInsights"
                heading="Previsão de conversão"
                unboxed
              >
                <span class="label label--large">
                  {{ predictedIncome | currency('integer') }}
                </span>
              </BasePaneCard>
            </BaseGrid>
          </BasePaneCard>

          <BasePaneCard
            heading="Lead Scoring"
            :isLoading="isLoadingInsights"
            class="grid-span-4"
          >
            <div class="leads-insight__lead-scoring">
            <BaseEmptyState v-if="isErrorInsights || !isLeadScoringPlatform">
              <SvgIconPersonSearch/>

              <h3>
                Você ainda não possui scores a serem exibidos.
              </h3>

              <p>Entre em contato com a nossa equipe.</p>
            </BaseEmptyState>

            <div
              v-else
            >
              <b-row class="leads-insight__scoring-titles">
                <b-col>
                  Lead Quality Score
                </b-col>

                <b-col>
                  % de leads
                </b-col>

                <b-col cols="5">
                  Resultado
                </b-col>
              </b-row>

              <hr v-if="isLeadScoringPlatform">

              <div
                v-for="item in leadScoringData"
                :key="item.grade"
                v-show="!isLoadingInsights"
              >
                <b-row>
                  <b-col class="leads-insight__lead-quality-score">
                    <b-col
                      cols="2"
                      class="leads-insight__scoring-grade"
                    >
                      {{ item.grade }}
                    </b-col>
                    <b-col>
                      <div
                        class="leads-insight__scoring-description"
                        :style="`background-color:rgba(152, 193, 221, ${item.opacity});`"
                      >
                        {{ item.description }}
                      </div>
                    </b-col>
                    <b-col
                      cols="5"
                      class="leads-insight__scoring-description-range"
                    >
                      {{ item.descriptionRange }}
                    </b-col>
                  </b-col>

                  <b-col class="leads-insight__scoring-leads-percent">
                    {{ Math.round(item.leadsPercent) }}%
                  </b-col>

                  <b-col cols="5">
                    <BaseStackedBar
                      :dataset="getStackedBar(item)"
                      hideLegend
                    />
                  </b-col>
                </b-row>
                <hr>
              </div>
              <b-row v-if="isLeadScoringPlatform">
                <b-col></b-col>
                <b-col cols="5">
                  <div class="leads-insight__scoring-legend">
                    <div class="leads-insight__scoring-legend-circle-conversion"></div>
                    <span class="leads-insight__scoring-legend-text">
                      Conversão
                    </span>
                  </div>

                  <div class="leads-insight__scoring-legend">
                    <div class="leads-insight__scoring-legend-circle-refusal"></div>
                    <span class="leads-insight__scoring-legend-text">
                      Recusa
                    </span>
                  </div>
                </b-col>
              </b-row>
            </div>
            </div>
          </BasePaneCard>
        </BaseGrid>
      </div>
    </template>
  </Component>
</template>

<script>
import BaseGrid from '@/components/base/BaseGrid'
import BasePaneCard from '@/components/base/BasePaneCard'
import CommonPageFilter from '@/components/common/CommonPageFilter'
import BaseStackedBar from '@/components/base/BaseStackedBar'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import { layoutMixin } from '@/utils/mixins'
import { numberFilter, currencyFilter, percentageFilter } from '@/utils/filters'
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'

export default {
  name: 'LeadsInsight',

  mixins: [
    layoutMixin
  ],

  components: {
    BaseGrid,
    BasePaneCard,
    CommonPageFilter,
    BaseStackedBar,
    BaseEmptyState
  },

  filters: {
    number: numberFilter,
    currency: currencyFilter,
    percentage: percentageFilter,

    leadsTotal (value) {
      const total = numberFilter(value)

      return value <= 1
        ? `${total} lead`
        : `${total} leads`
    }
  },

  data () {
    return {
      isLoadingImported: true,
      isLoadingQualified: true,
      isLoadingInsights: true,
      isErrorInsights: false,

      importedLeads: 0,
      qualifiedLeads: 0,
      predictedIncome: 0,
      predictedConversionPercent: 0,

      leadScoringData: []
    }
  },

  computed: {
    ...mapState('base', [
      'mainApi',
      'isLoading',
      'pageFilter'
    ]),

    ...mapGetters('auth', [
      'userId',
      'platformId'
    ]),

    isLeadScoringPlatform () {
      return [612, 618].includes(this.platformId)
    }
  },

  methods: {
    calcPercent(a, b) {
      if (b === 0) {
        return 0;
      } else {
        return a / b * 100;
      }
    },

    formatApiDate (date) {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, "0")
      const day = date.getDate().toString().padStart(2, "0")
      return `${year}-${month}-${day}`
    },

    returnPageFilters () {
      const today = new Date()
      let dateRange = `/${this.formatApiDate(today)}/${this.formatApiDate(today)}`
      let actionsArray = []

      if (this.pageFilter) {
        if (this.pageFilter.dateRange && this.pageFilter.dateRange.length > 0) {
          const startDate = this.pageFilter.dateRange[0]
          const endDate = this.pageFilter.dateRange[1]
          dateRange = `/${startDate}/${endDate}`
        }

        if (this.pageFilter.action && this.pageFilter.action.length > 0) {
          actionsArray = this.pageFilter.action
        }

        return [dateRange, actionsArray]
      }
    },

    async getImportedLeads () {
      try {
        this.isLoadingImported = true

        const path = 'lead/metrics/'
        const [dateRange, actionsArray] = this.returnPageFilters()
        const params = `?showFunnel=true&actionId=${actionsArray.join(',')}`

        const requestUrl = `${this.mainApi}${path}total_leads${dateRange}${params}`

        const response = await axios.get(requestUrl)

        this.importedLeads = response.data.value
      } catch (err) {
        console.error(err)
      } finally {
        this.isLoadingImported = false
      }
    },

    async getQualifiedLeads () {
      try {
        this.isLoadingQualified = true

        const path = 'lead/metrics/'
        const [dateRange, actionsArray] = this.returnPageFilters()
        const params = `?showFunnel=true&actionId=${actionsArray.join(',')}`

        const requestUrl = `${this.mainApi}${path}total_leads_valid${dateRange}${params}`

        const response = await axios.get(requestUrl)

        this.qualifiedLeads = response.data.value
      } catch (err) {
        console.error(err)
      } finally {
        this.isLoadingQualified = false
      }
    },

    processLeadScoringInsights (data) {
      const array = []

      const grades = [
        { grade: 'a', description: 'Muito bom', opacity: 1, descriptionRange: '75 - 100' },
        { grade: 'b', description: 'Bom', opacity: 0.75, descriptionRange: '50 - 74' },
        { grade: 'c', description: 'Médio', opacity: 0.5, descriptionRange: '25 - 49' },
        { grade: 'd', description: 'Ruim', opacity: 0.25, descriptionRange: '0 - 24' }
      ]

      const responseLength = Object.entries(data).length
      const isAllNumbers = Object.values(data).every(val => typeof val === 'number')

      if (responseLength && isAllNumbers) {
        for (const i in grades) {
          const grade = grades[i].grade

          const gradeTotal = data[`conversion_${grade}`] + data[`refusal_${grade}`]

          const leadsPercent = this.calcPercent(gradeTotal, data.total_refusal_and_conversion)

          const resultsPercent = {
            conversion: this.calcPercent(data[`conversion_${grade}`], gradeTotal),
            refusal: this.calcPercent(data[`refusal_${grade}`], gradeTotal)
          }

          const resultsAmount = {
            conversion: data[`conversion_${grade}`],
            refusal: data[`refusal_${grade}`]
          }

          array.push({
            grade: grade.toUpperCase(),
            description: grades[i].description,
            descriptionRange: grades[i].descriptionRange,
            opacity: grades[i].opacity,
            leadsPercent: leadsPercent,
            resultsPercent: resultsPercent,
            resultsAmount: resultsAmount
          })
        }
      }
      return array
    },

    async getLeadScoringInsights () {
      try {
        this.isLoadingInsights = true
        this.isErrorInsights = false

        const path = 'lead/score/insight'
        const [dateRange, actionsArray] = this.returnPageFilters()
        const params = `/${actionsArray.join(',')}`

        const requestUrl = `${this.mainApi}${path}${dateRange}${params}`
        const response = await axios.get(requestUrl)

        if (response && response.data) {
          this.predictedIncome = response.data.prevision_value
          this.predictedConversionPercent = this.calcPercent(response.data.prevision_qtd, response.data.total)

          const processedData = this.processLeadScoringInsights(response.data)
          this.leadScoringData = processedData
        } else {
          throw new Error('No insights data returned from server')
        }
      } catch (err) {
        this.isErrorInsights = true
        this.predictedIncome = 0
        this.predictedConversionPercent = 0
        console.error(err)
      } finally {
        this.isLoadingInsights = false
      }
    },

    getStackedBar (data) {
      const { resultsPercent,resultsAmount } = data

      return {
        percentage: [
          resultsPercent.conversion,
          resultsPercent.refusal
        ],

        label: [
          this.$options.filters.leadsTotal(resultsAmount.conversion),
          this.$options.filters.leadsTotal(resultsAmount.refusal)
        ],

        legend: [
          'Conversão',
          'Recusa'
        ]
      }
    }
  },

  watch: {
    pageFilter () {
      if (this.isLeadScoringPlatform) {
        this.getImportedLeads()
        this.getQualifiedLeads()
        this.getLeadScoringInsights()
      }
    }
  },

  created () {
    if (this.isLeadScoringPlatform) {
      this.getImportedLeads()
      this.getQualifiedLeads()
      this.getLeadScoringInsights()
    } else {
      this.isLoadingImported = false
      this.isLoadingQualified = false
      this.isLoadingInsights = false
    }
  }
}
</script>

<style lang="scss" scoped>
.leads-insight {
  $blue-dark: nth($colors-custom, 1);
  $blue-light: nth($colors-custom, 2);
  $color-muted: nth($colors-custom, 4);

  &__divider {
    border-right: 2px dashed $color-muted;
  }

  // Lead Scoring
  &__scoring-titles {
    margin: 30px 0 0 0;
    color: #000;
    font: {
      weight: 500;
      size: 13px;
    }
  }

  &__lead-quality-score {
    display: flex;
    align-items: center;
  }

  &__scoring-grade {
    width: 10% !important;
    color: $blue-dark;
    font: {
      weight: 600;
      size: 18px;
    }
  }

  &__scoring-description {
    display: inline-block;
    padding: 8px 10px;
    margin: 2px 1px;
    border-radius: 50px;
    color: $blue-dark;
    min-width: 81px;
    text-align: center;
    font: {
      weight: 500;
      size: 11px;
    }
  }

  &__scoring-description-range {
    color: $blue-light;
    font: {
      weight: 600;
      size: 18px;
    }
  }

  &__scoring-leads-percent {
    @extend .leads-insight__scoring-description-range;
    display: flex;
    align-items: center;
  }

  &__scoring-legend {
    display: inline-flex;
    align-items: center;
  }

  &__scoring-legend-circle {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 5px;
    &-conversion {
      @extend .leads-insight__scoring-legend-circle;
      background-color: $blue-dark;
    }
    &-refusal {
      @extend .leads-insight__scoring-legend-circle;
      background-color: $blue-light;
      margin-left: 30px;
    }
  }

  &__scoring-legend-text {
    font: {
      weight: 600;
      size: 12px;
    }
  }

  &__align-right {
    text-align: right;
  }
}
</style>
